// Iter54 — `/settings/tier` — tier management.
//
// Two modes:
//   * Tenant admin (any role): sees their own tier + usage + upgrade CTA.
//   * Platform admin: also sees a global tenants table with trial / tier
//     controls per row.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const TIER_COLOR = {
  freemium: "#c89adb",
  trial: "#f3b14a",
  full: "#9aebc2",
};

export default function TierSettings() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const [tierInfo, setTierInfo] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const isPlatformAdmin = user?.role === "platform_admin";

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auth/me/tier`);
      setTierInfo(r.data);
    } catch (e) {
      addToast({ type: "error", title: "Failed to load tier", body: e.message });
    }
    if (isPlatformAdmin) {
      try {
        // Iter54 fix — list endpoint lives on /api/tenants (routes/misc.py),
        // not /api/admin/tenants which only registers POST/PATCH/DELETE.
        const r = await axios.get(`${API}/tenants`);
        setTenants(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        addToast({ type: "error", title: "Failed to load tenants", body: e?.response?.data?.detail || e.message });
      }
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [isPlatformAdmin]);

  const grantTrial = async (tenantId, days) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tenants/${tenantId}/trial`, { days, trial_tier: "full" });
      addToast({ type: "success", title: `Trial granted (${days} days)` });
      await refresh();
    } catch (e) {
      addToast({ type: "error", title: "Failed to grant trial", body: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const revokeTrial = async (tenantId) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/tenants/${tenantId}/trial`);
      addToast({ type: "success", title: "Trial revoked" });
      await refresh();
    } catch (e) {
      addToast({ type: "error", title: "Failed to revoke trial", body: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const setTier = async (tenantId, tier) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tenants/${tenantId}/tier`, { tier });
      addToast({ type: "success", title: `Tier set to ${tier}` });
      await refresh();
    } catch (e) {
      addToast({ type: "error", title: "Failed to set tier", body: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tier-settings-page">
      <h1 style={{
        fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800,
        fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)",
        letterSpacing: "-0.5px", lineHeight: 1.05,
      }} data-testid="view-title">
        Tier & Plan
      </h1>
      <div className="mt-1.5 mb-6" style={mono(11, "var(--text-dim, #c5cad8)")}>
        Manage your subscription tier and view current usage
      </div>

      {/* My tier card */}
      <Section title="Your tenant" testId="tier-my-card">
        {tierInfo ? (
          <div
            className="rounded-md p-4"
            style={{ background: "var(--bg-surface, #0d0f18)", border: `1px solid ${TIER_COLOR[tierInfo.tier] || "rgba(255,255,255,0.10)"}55` }}
          >
            <div className="flex items-baseline gap-3 mb-3">
              <span style={mono(10, "var(--text-dim, #9aa1b3)", 700, "1.2px")} className="uppercase">Current tier</span>
              <span style={{ ...mono(22, TIER_COLOR[tierInfo.tier] || "#9aa1b3", 700), textTransform: "uppercase" }} data-testid="tier-my-current">
                {tierInfo.tier}
              </span>
              {tierInfo.trial_active && (
                <span style={mono(11, "#f3b14a", 700, "0.4px")}>
                  · {tierInfo.days_remaining} {tierInfo.days_remaining === 1 ? "day" : "days"} remaining
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {tierInfo.usage && Object.entries(tierInfo.usage).map(([k, u]) => (
                <UsageCard key={k} kind={k} used={u.used} limit={u.limit} blocked={tierInfo.tier === "freemium"} />
              ))}
            </div>
            {tierInfo.blocked_features?.length > 0 && (
              <div className="mt-4" data-testid="tier-blocked-features">
                <div className="mb-2 uppercase" style={mono(9, "var(--text-dim, #9aa1b3)", 700, "0.8px")}>Locked features</div>
                <div className="flex flex-wrap gap-1.5">
                  {tierInfo.blocked_features.map((f) => (
                    <span key={f} style={{
                      background: "rgba(155,89,182,0.10)", border: "1px solid rgba(155,89,182,0.30)",
                      color: "#c89adb", padding: "2px 7px", borderRadius: 3,
                      ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase",
                    }}>{f.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
            )}
            {tierInfo.tier === "freemium" && (
              <div className="mt-4 px-3 py-2 rounded"
                style={{ background: "rgba(24,95,165,0.10)", border: "1px dashed rgba(24,95,165,0.45)", ...mono(11, "#9ec3ec") }}
                data-testid="tier-upgrade-cta"
              >
                Need more? Email <a href="mailto:sales@sitroom.io" style={{ color: "#9ec3ec", textDecoration: "underline" }}>sales@sitroom.io</a> to upgrade to full SITROOM.
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md p-6" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)", ...mono(11, "var(--text-dim, #9aa1b3)") }}>Loading…</div>
        )}
      </Section>

      {/* Platform admin global table */}
      {isPlatformAdmin && (
        <Section title="All tenants" testId="tier-admin-card">
          <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
            <div className="flex items-center gap-3 px-3 py-2"
              style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <Hd flex>Tenant</Hd>
              <Hd w={110}>Current tier</Hd>
              <Hd w={130}>Trial until</Hd>
              <Hd w={260}>Actions</Hd>
            </div>
            {tenants.length === 0 ? (
              <div className="px-3 py-4 text-center" style={mono(11, "var(--text-dim, #9aa1b3)")}>No tenants.</div>
            ) : tenants.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 px-3 py-2.5"
                style={{ background: "var(--bg-surface, #0d0f18)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
                data-testid={`tier-admin-row-${t.id}`}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text, #f0f2f8)" }}>{t.name}</div>
                  <div style={mono(9, "var(--text-dim, #9aa1b3)")}>{t.id}{t.self_signup ? " · self-signup" : ""}</div>
                </div>
                <div style={{ width: 110 }}>
                  <span style={{ ...mono(10, TIER_COLOR[t.tier] || "#9aa1b3", 700, "0.5px"), textTransform: "uppercase" }}>
                    {t.tier || "—"}
                  </span>
                </div>
                <div style={{ width: 130 }}>
                  <span style={mono(10, "var(--text-dim, #9aa1b3)")}>
                    {t.trial_until ? new Date(t.trial_until).toLocaleDateString() : "—"}
                  </span>
                </div>
                <div style={{ width: 260 }} className="flex flex-wrap gap-1.5">
                  {t.tier !== "full" && (
                    <ActionBtn label="→ Full" onClick={() => setTier(t.id, "full")} disabled={busy} testId={`tier-set-full-${t.id}`} color="#4ed084" />
                  )}
                  {t.tier !== "freemium" && (
                    <ActionBtn label="→ Freemium" onClick={() => setTier(t.id, "freemium")} disabled={busy} testId={`tier-set-freemium-${t.id}`} color="#c89adb" />
                  )}
                  {t.tier !== "trial" && (
                    <ActionBtn label="14d trial" onClick={() => grantTrial(t.id, 14)} disabled={busy} testId={`tier-grant-trial-${t.id}`} color="#f3b14a" />
                  )}
                  {t.tier === "trial" && (
                    <ActionBtn label="Revoke trial" onClick={() => revokeTrial(t.id)} disabled={busy} testId={`tier-revoke-trial-${t.id}`} color="#E24B4A" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

const Section = ({ title, children, testId }) => (
  <div className="mb-8" data-testid={testId}>
    <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)", letterSpacing: "-0.2px" }} className="mb-3">{title}</h2>
    {children}
  </div>
);

const UsageCard = ({ kind, used, limit, blocked }) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const c = pct >= 100 ? "#E24B4A" : pct >= 80 ? "#f3b14a" : "#9aebc2";
  const labels = {
    fmea_active: "Active FMEAs",
    eightd_active: "Active 8D reports",
    incidents_monthly: "Incidents this month",
  };
  return (
    <div
      className="rounded-md p-3"
      style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={`tier-usage-${kind}`}
    >
      <div style={mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px")} className="uppercase mb-1">{labels[kind] || kind}</div>
      <div className="flex items-baseline gap-1">
        <span style={mono(20, c, 700)}>{used}</span>
        <span style={mono(11, "var(--text-dim, #9aa1b3)")}>/ {blocked ? limit : "∞"}</span>
      </div>
      {blocked && (
        <div className="mt-1.5" style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 999 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: c, borderRadius: 999 }} />
        </div>
      )}
    </div>
  );
};

const Hd = ({ children, flex, w }) => (
  <div style={{
    flex: flex ? 1 : undefined,
    width: w,
    ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"),
    textTransform: "uppercase",
  }}>{children}</div>
);

const ActionBtn = ({ label, onClick, disabled, testId, color }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className="px-2 py-1 rounded"
    style={{
      background: "transparent", border: `1px solid ${color}66`, color,
      ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase",
      cursor: disabled ? "wait" : "pointer", opacity: disabled ? 0.5 : 1,
    }}
    data-testid={testId}
  >{label}</button>
);
